import { Img } from "@/components/site/Img";
import { useState } from "react";
import { MessageCircle, Phone, Play, Share2, Store } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { VideoLightbox } from "./VideoLightbox";
import { ReserveModal } from "./ReserveModal";
import { PHONE_HREF, PHONE_DISPLAY, SMS_DRAFTS } from "@/lib/contact";
import { useSmsHref } from "./ContactButtons";
import { track } from "@/lib/track";

import { FB_USERNAME } from "@/lib/contact";
export { FB_USERNAME, MESSENGER_URL, PHONE_HREF, PHONE_DISPLAY } from "@/lib/contact";

export const birdBaths = [
  {
    title: "Scalloped Bird Bath",
    price: "$125",
    detail: "A wide fluted bowl on a turned pedestal. A classic that fits any garden. $125, finishes customizable.",
    image: "/images/bird-bath-scalloped.jpg",
    alt: "Scalloped concrete bird bath handcrafted in Villa Rica, GA — $125",
    testid: "bird-bath-scalloped",
  },
  {
    title: "Petal Bird Bath",
    price: "$60",
    detail: "A flower-shaped bowl on a carved pedestal. A softer, sweeter silhouette. $60, finishes customizable.",
    image: "/images/bird-bath-petal.jpg",
    alt: "Petal flower-shaped concrete bird bath handcrafted in Villa Rica, GA — $60",
    testid: "bird-bath-petal",
  },
  {
    title: "Cherub Birdbath Set",
    detail: "A cherub leaning on a scallop birdbath, with cherubs, angels, and gnomes to pair with it. Pick the set, or just the pieces you love.",
    image: "/images/cherubs-collection.jpg",
    alt: "Cherub birdbath set with angel and gnome statues in Villa Rica, GA",
    testid: "bird-bath-cherub-set",
  },
  {
    title: "Lotus Bowl Bird Bath",
    detail: "Fresh from the mold: a lotus petal bowl with a leaf and berry basin, set on a classic baluster pedestal.",
    image: "/images/bird-bath-lotus-bowl.jpg",
    alt: "Fresh cast concrete lotus bowl bird bath on a baluster pedestal in Villa Rica, GA",
    testid: "bird-bath-lotus-bowl",
  },
];

export const yardPieces = [
  {
    title: "Pineapple Fountain in Charcoal",
    detail: "The classic two-tier pineapple fountain finished in a deep charcoal slate. Just finished. Tap the small photo for a closer look at the pineapple topper. Ask about pricing and delivery.",
    image: "/images/pineapple-charcoal-fountain.jpg",
    altImage: "/images/pineapple-charcoal-detail.jpg",
    alt: "Two-tier concrete pineapple fountain in a charcoal slate finish, available in Villa Rica, GA",
    testid: "available-pineapple-charcoal",
    isNew: true,
    atRedDoor: true,
  },
  {
    title: "Angel Shell Fountain",
    detail: "A winged angel standing in a scalloped shell bowl over a wide petal basin. Just posted. Ask about pricing and delivery. Finishes customizable.",
    image: "/images/angel-shell-fountain.jpg",
    alt: "Winged angel fountain standing in scalloped shell bowls, available in Villa Rica, GA",
    testid: "available-angel-fountain",
  },
  {
    title: "Cherub with Jug",
    detail: "A sweet weathered cherub cradling a water jug, ready to stand alone or top a fountain. Ask about pricing and delivery. Finishes customizable.",
    image: "/images/cherub-jug.jpg",
    alt: "Weathered concrete cherub holding a water jug, available in Villa Rica, GA",
    testid: "available-cherub-jug",
  },
  {
    title: "Elephant Fountain",
    detail: "A playful elephant spraying from its trunk atop two scalloped shell bowls. Tap the video for a closer look at the details. Ask about pricing and delivery. Finishes customizable.",
    image: "/images/elephant-fountain.jpg",
    video: "elephant-fountain-run.mp4",
    videoLabel: "See more details",
    alt: "Two-tier elephant fountain with scalloped shell bowls, available in Villa Rica, GA",
    testid: "available-elephant-fountain",
  },
  {
    title: "Cherub Pour Fountain",
    detail: "A cherub pouring from his jug over two scalloped shell bowls, shown fresh out of the mold and finished in bright white. Tap the small photo to see both stages. Finishes customizable.",
    image: "/images/cherub-pour-final.jpg",
    altImage: "/images/cherub-pour-green.jpg",
    alt: "Two-tier cherub pour fountain with scalloped shell bowls finished in white, available in Villa Rica, GA",
    testid: "available-cherub-pour",
  },
  {
    title: "Two-Tier Fountain",
    price: "$450",
    detail: "Our two-tier fountain with a swirled pedestal and fluted basin, shown in natural gray and teal. Pick your topper (pineapple, tulip, or cherub) and any finish. $450, fully customizable.",
    image: "/images/available-two-tier-fountain.jpg",
    video: "teal-fountain-run.mp4",
    alt: "Two-tier concrete fountains in natural gray and teal with pineapple toppers, available in Villa Rica, GA — $450",
    testid: "available-two-tier-fountain",
  },
  {
    title: "Tulip Topper Option",
    price: "$450",
    detail: "The same two-tier fountain with a tulip topper for a softer, garden-fresh look. Shown in white against teal, but yours can be any color. $450, fully customizable.",
    image: "/images/available-topper-tulip.jpg",
    alt: "White tulip topper option on a teal two-tier concrete fountain — $450",
    testid: "available-topper-tulip",
  },
  {
    title: "Cherub Topper Option",
    price: "$450",
    detail: "The two-tier fountain with a sweet cherub topper, shown in a bright teal finish. $450, fully customizable.",
    image: "/images/teal-fountain-installed.jpg",
    alt: "Cherub topper option on a bright teal two-tier concrete fountain — $450",
    testid: "available-topper-cherub",
  },
  {
    title: "Grand Urn Planter",
    detail: "A large egg-and-dart urn planter with a melon-ribbed body on a footed pedestal. A substantial classic for a porch, driveway, or garden entrance.",
    image: "/images/planter-grand-urn.jpg",
    imgPos: "object-top",
    alt: "Large egg-and-dart concrete urn planter available in Villa Rica, GA",
    testid: "available-planter-grand-urn",
  },
  {
    title: "Lotus Planters on Pedestals",
    price: "$85 each",
    detail: "Lotus-petal urn planters on tall carved pedestals with fruit and flower detail. $85 each, lovely solo or flanking a walkway as a pair.",
    image: "/images/planter-pedestal-pair.jpg",
    imgPos: "object-top",
    alt: "Pair of lotus concrete planters on carved pedestals, $85 each, in Villa Rica, GA",
    testid: "available-planter-pedestal",
  },
  {
    title: "Fluted Urn Planter",
    price: "$45",
    detail: "A fluted urn planter with a flower-band rim on a square footed base. $45. Ask about current availability.",
    image: "/images/planter-fluted-urn.jpg",
    imgPos: "object-top",
    alt: "Fluted concrete urn planter with flower band rim, $45, in Villa Rica, GA",
    testid: "available-planter-fluted",
  },
  {
    title: "Bamboo Panel Planter",
    price: "$20",
    detail: "A square tapered planter with bamboo and floral relief panels on every side. Fresh out of the mold. $20. Ask about current availability.",
    image: "/images/planter-bamboo-panel.jpg",
    alt: "Square concrete planter with bamboo relief panels, handcrafted in Villa Rica, GA",
    testid: "available-planter-bamboo",
  },
  {
    title: "Carved Bowl Planter",
    detail: "A round bowl planter with floral scenes and a Greek key band, shown on a matching footed base. Ask about pairing it with a pedestal.",
    image: "/images/planter-carved-bowl.jpg",
    alt: "Round carved concrete bowl planter on a footed base, handcrafted in Villa Rica, GA",
    testid: "available-planter-carved-bowl",
  },
  {
    title: "Bowl on Carved Pedestal",
    detail: "A carved bowl planter set on a tall carved pedestal column. Two pot-and-pedestal combos available. Tap the small photo to see the other. A stately pairing for an entry or a garden corner.",
    image: "/images/carved-planter-pedestal.jpg",
    altImage: "/images/carved-planter-rose-pedestal.jpg",
    alt: "Carved concrete bowl planter on an ornate footed stand and carved pedestal in Villa Rica, GA",
    testid: "available-planter-bowl-pedestal",
    isNew: true,
  },
  {
    title: "Ball Finials (Pair)",
    price: "$125 pair",
    detail: "Two solid concrete balls, salvaged and ready to top a column, gate post, or garden wall. $125 for the pair, first come, first served. Pickup in Villa Rica.",
    image: "/images/ball-finials.jpg",
    alt: "Two solid concrete ball finials, $125 for the pair, pickup in Villa Rica, GA",
    testid: "available-ball-finials",
  },
  {
    title: "Pagoda Lantern",
    price: "$75",
    detail: "A Japanese style garden lantern, hand cast in Villa Rica. A peaceful accent for a path, a pond edge, or a zen corner. $75.",
    image: "/images/catalog/catalog-03.jpg",
    alt: "Japanese style concrete pagoda garden lantern, $75, in Villa Rica, GA",
    testid: "available-pagoda-lantern",
  },
  {
    title: "Six-Sided Garden Lantern",
    price: "$65",
    detail: "A six-sided pagoda style lantern with a peaked, finial-topped roof over an open lantern box, set on a footed base with scroll relief. Hand cast in natural concrete gray in Villa Rica. Shown on one of our carved pedestals, which is a separate piece. $65.",
    image: "/images/garden-lantern-hex.jpg",
    alt: "Six-sided concrete pagoda style garden lantern with finial roof on a footed base, $65, in Villa Rica, GA",
    testid: "available-garden-lantern",
    isNew: true,
  },
  {
    title: "Carved Pedestal Pair",
    detail: "A pair of square pedestals with flower urn relief on every side. Perfect under a planter, a statue, or a gazing ball.",
    image: "/images/carved-pedestals-pair.jpg",
    alt: "Pair of carved concrete pedestals with flower urn relief in Villa Rica, GA",
    testid: "available-carved-pedestals",
  },
  {
    title: "Baby Elephant Statue",
    price: "$65",
    detail: "A playful baby elephant with its trunk curled high for good luck, finished in a charcoal antiqued wash. Tap the small photo to see it in natural gray. A sweet accent for a porch step or garden bed. $65, either finish.",
    image: "/images/baby-elephant-charcoal.jpg",
    altImage: "/images/baby-elephant.jpg",
    alt: "Concrete baby elephant statue with trunk raised in a charcoal antiqued finish, $65, in Villa Rica, GA",
    testid: "available-baby-elephant",
    isNew: true,
    atRedDoor: true,
  },
  {
    title: "Cherub Petal Fountain",
    price: "$975",
    detail: "A hand-cast cherub stands on a wide petal bowl atop a twisted pedestal, set in a round scalloped basin. Tap the small photo for a second view. A soft, classical single-tier fountain. Ask about current availability.",
    image: "/images/cherub-petal-fountain-painted.jpg",
    altImage: "/images/cherub-petal-fountain.jpg",
    altLabel: "See the other photo",
    alt: "Concrete cherub fountain with petal bowl, twisted pedestal, and scalloped round basin, $975, in Villa Rica, GA",
    testid: "available-cherub-petal-fountain",
    isNew: true,
  },
  {
    title: "Crowned Lion Pair",
    detail: "A matched pair of regal sitting lions wearing crowns, freshly cast in bright white. Bold guardians for a doorway, gate, or drive, sold as a pair or singly. Tap the small photo for the antiqued finish option.",
    image: "/images/crowned-lions-pair.jpg",
    altImage: "/images/crowned-lion.jpg",
    alt: "Matched pair of concrete crowned lion statues in bright white in Villa Rica, GA",
    testid: "available-crowned-lion",
    isNew: true,
    atRedDoor: true,
  },
  {
    title: "Foo Dog Guardians (Pair)",
    detail: "A pair of foo dogs, the legendary Asian guardian lion-dragons, always cast as a set to watch over an entrance. Freshly cast. Ask about current availability.",
    image: "/images/foo-dogs-pair.jpg",
    alt: "Pair of concrete foo dog guardian statues in Villa Rica, GA",
    testid: "available-foo-dogs",
    isNew: true,
  },
  {
    title: "Lady with Flower",
    detail: "A graceful draped lady holding a flower, cast in bright white. A soft romantic centerpiece for a rose bed or courtyard.",
    image: "/images/lady-flower.jpg",
    alt: "White concrete statue of a draped lady holding a flower in Villa Rica, GA",
    testid: "available-lady-flower",
  },
  {
    title: "Lady by the Sea",
    price: "On Sale",
    detail: "A classical lady with a dolphin at her feet, in a naturally weathered finish that looks like old estate stone. Two are available, a little worn but not too bad, and both on sale. Call or message for the sale price.",
    image: "/images/lady-dolphin.jpg",
    alt: "Weathered concrete statue of a classical lady with a dolphin in Villa Rica, GA",
    testid: "available-lady-dolphin",
  },
  {
    title: "Egg & Dart Bowl Planter",
    detail: "A wide low bowl planter with an egg and dart rim over a melon ribbed body. Two fresh casts were made; ask about current availability. Beautiful filled with annuals or succulents.",
    image: "/images/egg-dart-fresh-pair.jpg",
    altImage: "/images/bowl-egg-dart.jpg",
    alt: "Two fresh cast concrete bowl planters with egg and dart rims in Villa Rica, GA",
    testid: "available-bowl-egg-dart",
    isNew: true,
  },
  {
    title: "Garden Gnome",
    price: "$15",
    detail: "A grumpy old garden gnome with boots and a long beard, shown in an antiqued whitewash finish and fresh out of the mold. Tap the small photo to see both. The cheapest guard you'll ever hire. $15, either finish.",
    image: "/images/gnome-antiqued.jpg",
    altImage: "/images/garden-gnome.jpg",
    alt: "Concrete garden gnome statue in an antiqued whitewash finish, $15, in Villa Rica, GA",
    testid: "available-garden-gnome",
    isNew: true,
  },
  {
    title: "Cherub Shell Dish",
    price: "$15",
    detail: "A winged cherub holding a scalloped shell dish, finished in a soft antiqued whitewash. Fill it with seed or water for a tiny bird feeder, or tuck it into a flower bed. $15.",
    image: "/images/cherub-shell-dish.jpg",
    alt: "Concrete cherub statue holding a scalloped shell dish in an antiqued finish, available in Villa Rica, GA",
    testid: "available-cherub-shell-dish",
    isNew: true,
  },
  {
    title: "Rose Lady Bust",
    detail: "A serene lady with roses braided through her hair, fresh out of the mold in natural gray. Pairs with the standing Rose Lady statue, or stands alone on a pedestal. Finishes customizable.",
    image: "/images/rose-lady-bust.jpg",
    alt: "Concrete bust of a lady with roses in her hair, fresh cast in natural gray, available in Villa Rica, GA",
    testid: "available-rose-lady-bust",
    isNew: true,
  },
  {
    title: "Boy & Goose Statue",
    detail: "A vintage hand-cast boy holding a goose, with decades of weathered patina and traces of old blue paint. Full of character as-is, or Chris can refinish it in any color.",
    image: "/images/available-boy-goose.jpg",
    alt: "Concrete boy wrestling a goose garden statue available in Villa Rica, GA",
    testid: "available-boy-goose",
  },
  {
    title: "Cast Stone Horse Head",
    detail: "A striking detailed horse head. Beautiful as a doorway accent, garden gate piece, or paired statement on a driveway.",
    image: "/images/horse-head.jpg",
    alt: "Cast stone horse head sculpture for a garden or entryway in Villa Rica, GA",
    testid: "available-horse",
  },
  {
    title: "Lotus Tier Fountain",
    detail: "Two-tier flower-petal fountain with a stone lotus topper. Soft, elegant water feature for a flower bed or front yard.",
    image: "/images/lotus-tier-fountain.jpg",
    alt: "Tiered concrete lotus fountain handcrafted in Villa Rica, GA",
    testid: "available-lotus",
  },
  {
    title: "Standing Garden Angel",
    detail: "Hand-cast standing angel with detailed wings and drapery. Lovely solo, or as a pair flanking a path or memorial bed.",
    image: "/images/angel-standing.jpg",
    alt: "Standing concrete garden angel statue with folded wings in Villa Rica, GA",
    testid: "available-angel-standing",
  },
  {
    title: "Sitting Cherub",
    detail: "Sweet small cherub, perfect for a flower bed, bird bath, or quiet corner of the garden.",
    image: "/images/cherub-sitting.jpg",
    alt: "Sitting concrete cherub garden statue handcrafted in Villa Rica, GA",
    testid: "available-cherub-sitting",
  },
  {
    title: "Sitting Buddha Fountain",
    detail: "Compact, calming water feature that fits a small patio, courtyard, or zen corner of the yard.",
    image: "/images/buddha-fountain.jpg",
    alt: "Sitting Buddha concrete water fountain for a patio or zen garden in Villa Rica, GA",
    testid: "available-buddha",
  },
  {
    title: "Buddha with Offering Bowl",
    price: "$85",
    detail: "A serene sitting Buddha holding an offering bowl, one hand raised in blessing. Freshly cast, $85. Ask about current availability.",
    image: "/images/buddha-sitting-bowl.jpg",
    altImage: "/images/red-door-buddha.jpg",
    alt: "Concrete sitting Buddha statue holding an offering bowl, $85, in Villa Rica, GA",
    testid: "available-buddha-bowl",
    isNew: true,
    atRedDoor: true,
  },
  {
    title: "Cherub Shell Fountain",
    detail: "A graceful 2-tier scallop-shell fountain topped with a hand-cast cherub. A soft, classical centerpiece, with standing statues available alongside it.",
    image: "/images/cherub-shell-fountain.jpg",
    alt: "Concrete cherub and shell garden fountain available in Villa Rica, GA",
    testid: "available-cherub-shell",
  },
  {
    title: "4-Tier Pineapple Fountain",
    detail: "A tall 4-tier fountain crowned with a pineapple finial, with acanthus leaf detail and a wide fluted basin. A grand, welcoming centerpiece. Ask about current availability.",
    image: "/images/pineapple-tier-fountain.jpg",
    alt: "Four tier concrete pineapple topper fountain handcrafted in Villa Rica, GA",
    testid: "available-pineapple-tier",
  },
  {
    title: "Squirrel",
    detail: "A whimsical hand-cast squirrel with a big sweeping tail, a sweet surprise for a flower bed, porch step, or quiet garden corner.",
    image: "/images/available-squirrel.jpg",
    alt: "Small concrete squirrel garden statue available in Villa Rica, GA",
    testid: "available-squirrel",
  },
];

export const ALL_PIECES = [...yardPieces, ...birdBaths];
export const pieceSlug = (p) => p.testid.replace(/^available-/, "");

const UI_SENTENCE = /^(tap|click|see|swipe)\b/i;
export const seoDescription = (p) => {
  const priced = p.price && p.price !== "Ask for pricing";
  const sentences = (p.detail || "").split(/(?<=[.!?])\s+/).filter((t) => t && !UI_SENTENCE.test(t));
  let out = priced ? `${p.title}, ${p.price}.` : `${p.title}.`;
  for (const t of sentences) {
    if (/^\$?\d/.test(t) || (priced && t.includes(p.price))) continue;
    if ((out + " " + t).length > 155) break;
    out += " " + t;
  }
  if (!/ask about current availability/i.test(out) && (out + " Ask about current availability.").length <= 155) out += " Ask about current availability.";
  return out;
};

export const findPieceBySlug = (slug) => ALL_PIECES.find((p) => pieceSlug(p) === slug);

export const PRICE_BY_TESTID = {
  "bird-bath-scalloped": "125",
  "available-buddha-bowl": "85",
  "available-cherub-petal-fountain": "975",
  "available-baby-elephant": "65",
  "available-cherub-shell-dish": "15",
  "available-garden-gnome": "15",
  "bird-bath-petal": "60",
  "available-planter-pedestal": "85",
  "available-planter-fluted": "45",
  "available-planter-bamboo": "20",
  "available-ball-finials": "125",
  "available-pagoda-lantern": "75",
  "available-garden-lantern": "65",
  "available-two-tier-fountain": "450",
  "available-topper-tulip": "450",
  "available-topper-cherub": "450",
};

const CAT_BY_TESTID = {
  "available-cherub-petal-fountain": "fountain",
  "available-cherub-shell-dish": "statue",
  "available-rose-lady-bust": "statue",
  "available-pineapple-charcoal": "fountain",
  "available-angel-fountain": "fountain",
  "available-cherub-jug": "statue",
  "available-elephant-fountain": "fountain",
  "available-cherub-pour": "fountain",
  "available-two-tier-fountain": "fountain",
  "available-topper-tulip": "fountain",
  "available-topper-cherub": "fountain",
  "available-lotus": "fountain",
  "available-buddha": "fountain",
  "available-buddha-bowl": "statue",
  "available-cherub-shell": "fountain",
  "available-pineapple-tier": "fountain",
  "bird-bath-scalloped": "birdbath",
  "bird-bath-petal": "birdbath",
  "bird-bath-cherub-set": "birdbath",
  "bird-bath-lotus-bowl": "birdbath",
  "available-baby-elephant": "statue",
  "available-crowned-lion": "statue",
  "available-foo-dogs": "statue",
  "available-garden-gnome": "statue",
  "available-lady-flower": "statue",
  "available-lady-dolphin": "statue",
  "available-boy-goose": "statue",
  "available-horse": "statue",
  "available-angel-standing": "statue",
  "available-cherub-sitting": "statue",
  "available-squirrel": "statue",
  "available-planter-grand-urn": "planter",
  "available-planter-pedestal": "planter",
  "available-planter-fluted": "planter",
  "available-planter-bamboo": "planter",
  "available-planter-carved-bowl": "planter",
  "available-planter-bowl-pedestal": "planter",
  "available-bowl-egg-dart": "planter",
  "available-ball-finials": "accent",
  "available-pagoda-lantern": "accent",
  "available-garden-lantern": "accent",
  "available-carved-pedestals": "accent",
};
export const pieceCat = (p) => CAT_BY_TESTID[p.testid] || "accent";
const CAT_FALLBACK_PRICE = {
  fountain: "$450 and up",
  statue: "$60 and up",
  planter: "$45 and up",
  birdbath: "$60 and up",
  accent: "$75 and up",
};
export const fallbackPrice = (p) => CAT_FALLBACK_PRICE[pieceCat(p)] || "$60 and up";
export const CATEGORY_LABELS = {
  fountain: "Fountains",
  birdbath: "Bird Baths",
  statue: "Statues",
  planter: "Planters",
  accent: "Garden Accents",
};

export const sharePiece = async (piece) => {
  const url = `${window.location.origin}/piece/${pieceSlug(piece)}`;
  track("share_click", piece.title);
  if (navigator.share) {
    try {
      await navigator.share({ title: piece.title, url });
      return;
    } catch {
      /* user cancelled or unsupported; fall through to copy */
    }
  }
  try {
    await navigator.clipboard.writeText(url);
    toast.success("Link copied! Paste it on Facebook or in a message.");
  } catch {
    toast(url);
  }
};

export const RedDoorNote = ({ testid }) => (
  <p
    data-testid={`${testid}-red-door-note`}
    className="-mt-3 mb-5 inline-flex items-center gap-1.5 text-[11px] tracking-[0.12em] uppercase text-[#7A2E22]"
  >
    <Store size={12} strokeWidth={1.5} aria-hidden="true" />
    Photographed at Red Door Antiques, LaGrange
  </p>
);

export const PieceCard = ({ piece }) => {
  const productSms = useSmsHref(SMS_DRAFTS.product(piece.title, pieceSlug(piece)));
  const [showVideo, setShowVideo] = useState(false);
  const [showAlt, setShowAlt] = useState(false);
  const [showReserve, setShowReserve] = useState(false);
  return (
  <article
    data-testid={piece.testid}
    className="card-lift group bg-[#EFEBE1] border border-[#D5CEBD] flex flex-col"
  >
    <div className="zoom-container relative w-full aspect-[4/5] overflow-hidden bg-[#D5CEBD]">
      <Link
        to={`/piece/${pieceSlug(piece)}`}
        data-testid={`${piece.testid}-detail-link`}
        aria-label={`View details for ${piece.title}`}
        className="block w-full h-full"
      >
        <Img
          src={showAlt && piece.altImage ? piece.altImage : piece.image}
          alt={piece.alt || piece.title}
          loading="lazy"
          sizes="(min-width: 1024px) 30vw, (min-width: 640px) 50vw, 100vw"
          className="w-full h-full object-cover object-top"
        />
      </Link>
      <div
        data-testid={`${piece.testid}-badge`}
        className={`absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1.5 text-[10px] tracking-[0.28em] uppercase pointer-events-none ${
          "bg-[#1F1E1C]/85 text-[#F9F6F0]"
        }`}
      >
        <span className="w-1.5 h-1.5 rounded-full blink-dot bg-[#3FA34D]" />
        {piece.isNew ? "Just In" : "Check availability"}
      </div>
      <button
        type="button"
        onClick={() => sharePiece(piece)}
        data-testid={`${piece.testid}-share`}
        aria-label={`Share ${piece.title}`}
        className="absolute top-4 right-4 w-9 h-9 bg-[#1F1E1C]/85 text-[#F9F6F0] flex items-center justify-center hover:bg-[#7A6547] transition-colors"
      >
        <Share2 size={14} strokeWidth={1.5} />
      </button>
      {piece.altImage && (
        <button
          type="button"
          onClick={() => setShowAlt((s) => !s)}
          data-testid={`${piece.testid}-alt-toggle`}
          aria-label={showAlt ? "See the main photo" : piece.altLabel || "See the alternate view"}
          className="absolute bottom-3 right-3 w-16 h-16 border-2 border-white/80 overflow-hidden shadow-lg transition-transform duration-300 hover:scale-105"
        >
          <Img
            src={showAlt ? piece.image : piece.altImage}
            alt={`${piece.title} alternate view`}
            loading="lazy"
            sizes="64px"
            className="w-full h-full object-cover"
          />
        </button>
      )}
      {piece.video && (
        <button
          type="button"
          onClick={() => setShowVideo(true)}
          data-testid={`${piece.testid}-play`}
          aria-label={`Watch the ${piece.title} running`}
          className="absolute bottom-3 right-3 inline-flex items-center gap-2.5 pl-2 pr-4 py-2 bg-[#1F1E1C]/85 border border-white/50 text-white text-[10px] tracking-[0.22em] uppercase backdrop-blur-sm transition-colors duration-300 hover:bg-[#7A6547]"
        >
          <span className="w-8 h-8 rounded-full border border-white/60 flex items-center justify-center flex-shrink-0">
            <Play size={12} strokeWidth={1.5} fill="currentColor" className="ml-0.5" />
          </span>
          {piece.videoLabel || "Watch it run"}
        </button>
      )}
      {piece.video && (
        <VideoLightbox
          open={showVideo}
          onClose={() => setShowVideo(false)}
          video={{
            mp4: `${process.env.REACT_APP_BACKEND_URL}/api/media/${piece.video}`,
            poster: piece.image,
            title: piece.title,
            portrait: true,
          }}
        />
      )}
    </div>
    <div className="p-6 md:p-7 flex flex-col flex-1">
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <h3 className="font-serif text-xl md:text-2xl text-[#2C2A28] font-normal">
          <Link
            to={`/piece/${pieceSlug(piece)}`}
            className="hover:text-[#7A6547] transition-colors"
          >
            {piece.title}
          </Link>
        </h3>
        <span
          data-testid={`${piece.testid}-price`}
          className={
            piece.price && piece.price !== "Ask for pricing"
              ? "text-[#7A6547] font-medium whitespace-nowrap"
              : "text-[#8A867F] text-xs tracking-[0.12em] uppercase whitespace-nowrap"
          }
        >
          {piece.price || fallbackPrice(piece)}
        </span>
      </div>
      <p className="text-[#5C5955] text-sm leading-relaxed mb-6 flex-1">
        {piece.detail}
      </p>
      {piece.atRedDoor && <RedDoorNote testid={piece.testid} />}
      <div className="grid grid-cols-2 gap-2 mb-2">
        <a
          href={PHONE_HREF}
          onClick={() => track("call_click", piece.title)}
          data-testid={`${piece.testid}-call`}
          aria-label={`Call Chris at ${PHONE_DISPLAY}`}
          className="inline-flex items-center justify-center gap-1.5 py-3 min-h-[44px] bg-[#1F1E1C] text-[#F9F6F0] text-[11px] tracking-[0.18em] uppercase hover:bg-[#7A6547] transition-colors"
        >
          <Phone size={12} strokeWidth={1.5} aria-hidden="true" />
          Call Chris
        </a>
        <a
          href={productSms}
          onClick={() => track("text_click", piece.title)}
          data-testid={`${piece.testid}-text`}
          aria-label={`Text Chris about the ${piece.title}`}
          className="inline-flex items-center justify-center gap-1.5 py-3 min-h-[44px] border border-[#7A6547] text-[#7A6547] text-[11px] tracking-[0.18em] uppercase hover:bg-[#7A6547] hover:text-[#F9F6F0] transition-colors"
        >
          <MessageCircle size={12} strokeWidth={1.5} aria-hidden="true" />
          Text Chris
        </a>
      </div>
      <button
        type="button"
        onClick={() => {
          setShowReserve(true);
          track("reserve_open", piece.title);
        }}
        data-testid={`${piece.testid}-reserve`}
        className="w-full py-3 border border-[#D5CEBD] text-[#5C5955] text-[11px] tracking-[0.2em] uppercase hover:border-[#7A6547] hover:text-[#7A6547] transition-colors"
      >
        Inquire About This Piece
      </button>
    </div>
    <ReserveModal
      open={showReserve}
      onClose={() => setShowReserve(false)}
      piece={piece.title}
      mode="availability"
      smsBody={SMS_DRAFTS.product(piece.title, pieceSlug(piece))}
    />
  </article>
  );
};

export const YardTourButton = ({ className = "" }) => {
  const [show, setShow] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setShow(true)}
        data-testid="yard-tour-button"
        className={`inline-flex items-center gap-2.5 pl-2 pr-5 py-2 border border-[#7A6547]/60 text-[#7A6547] text-[11px] tracking-[0.2em] uppercase hover:bg-[#7A6547] hover:text-[#F9F6F0] transition-colors ${className}`}
      >
        <span className="w-8 h-8 rounded-full border border-current flex items-center justify-center flex-shrink-0">
          <Play size={12} strokeWidth={1.5} fill="currentColor" className="ml-0.5" />
        </span>
        Take a quick tour of the yard
      </button>
      <VideoLightbox
        open={show}
        onClose={() => setShow(false)}
        video={{
          mp4: `${process.env.REACT_APP_BACKEND_URL}/api/media/yard-tour.mp4`,
          poster: "/images/yard-tour-poster.jpg",
          title: "A quick tour of the yard",
          portrait: true,
        }}
      />
    </>
  );
};

export const YardBanner = () => (
  <a
    href={`https://www.facebook.com/${FB_USERNAME}`}
    target="_blank"
    rel="noopener noreferrer"
    data-testid="available-yard-banner"
    className="zoom-container relative block w-full mb-10 md:mb-12 aspect-[21/9] overflow-hidden bg-[#D5CEBD] group"
    aria-label="Open the Facebook page to see the full yard inventory"
  >
    <Img
      src="/images/yard-banner-wide.jpg"
      alt="A view of fountains, statues, and pieces in the yard at Villa Rica"
      className="w-full h-full object-cover"
      loading="lazy"
    />
    <div className="absolute inset-0 bg-[#1F1E1C]/35" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#1F1E1C]/90 via-[#1F1E1C]/30 to-transparent" />
    <div className="absolute bottom-5 md:bottom-8 left-5 md:left-10 right-5 md:right-10 flex flex-col md:flex-row md:items-end md:justify-between gap-3 text-[#F9F6F0]">
      <div>
        <div className="text-[10px] tracking-[0.32em] uppercase text-[#C8B387] mb-2">
          More to see
        </div>
        <div className="font-serif font-light text-2xl md:text-3xl lg:text-4xl leading-tight max-w-2xl">
          The full lineup of fountains and sculptures.
        </div>
      </div>
      <span className="text-[11px] tracking-[0.24em] uppercase border-b border-white/70 pb-0.5 self-start md:self-auto">
        See more on Facebook →
      </span>
    </div>
  </a>
);
